import React from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { Check } from 'lucide-react'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { IconBadge } from 'components/UI/IconBadge'
import type { CertificateCustomer } from 'types'
import { font } from 'settings/tokens';
import { containerRadius, tileRadius } from 'settings/brandRadius';
import { glassSx } from 'utils/glass'

type SuccessOwnershipProps = {
  open: boolean
  onClose: () => void
  certificate: CertificateCustomer
  onViewCertificate?: () => void
  onCloseAll?: () => void
}

export const SuccessOwnership: React.FC<SuccessOwnershipProps> = ({ open, onClose, certificate, onViewCertificate, onCloseAll }) => {
  return (
    <DrawerPopup
      title="Transfer ownership"
      rightActionLabel="Done"
      rightActionOnClick={() => {
        onCloseAll ? onCloseAll() : onClose()
      }}
      handleOpen={open}
      handleClose={onClose}
      child={
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 480, height: '100%' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 4 }}>
            {/*
              The shared `IconBadge` (§36). This was a SIXTH copy of the same
              badge and a character-for-character duplicate of the one in
              `components/giftSendDrawer` — same 88px grey disc, same inline
              check SVG, same `mt: 0.5` nudge, same `mb: 3`. The SVG path was
              lucide's `Check` verbatim, so the glyph is unchanged; what this
              gains is the faint border the two success screens disagreed on.
            */}
            <IconBadge icon={Check} strokeWidth={2} sx={{ mb: 3 }} />

            <Stack spacing={1} alignItems="center">
              <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>Ownership transferred</Typography>
              <Typography sx={theme => ({ fontSize: '14px', fontWeight: 400, color: theme.palette.text.secondary, maxWidth: 280 })}>
                A notification to confirm ownership is sent to the recipient. You can cancel this transfer anytime before the recipient claims ownership.
              </Typography>
            </Stack>
          </Box>

          <Box sx={theme => ({ px: 2, pb: 2 })}>
            <Box
              sx={theme => ({
                p: 2,
                backgroundColor: theme.palette.grey[50],
                // VRG-562: follows the tenant's Corner radius.
                borderRadius: containerRadius(theme),
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              })}
            >
              <Box
                sx={theme => ({
                  width: 48,
                  height: 48,
                  backgroundColor: theme.palette.grey[300],
                  // VRG-562: follows the tenant's Corner radius. Clamped to the tile's 48px.
                  borderRadius: tileRadius(48),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                })}
              >
                {certificate.product.images?.[0] && (
                  <img src={certificate.product.images[0]} alt={certificate.product.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>{certificate.product.title}</Typography>
                <Typography sx={theme => ({ fontSize: `${font.label}px`, color: theme.palette.text.secondary })}>{certificate.product.sku}</Typography>
              </Box>
            </Box>

            <Box sx={{ height: 56 }} />
          </Box>

          <Box sx={theme => ({ position: 'sticky', bottom: 0, left: 0, right: 0, p: 2, borderTop: `1px solid ${theme.palette.divider}`, ...glassSx(theme) })}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                onCloseAll ? onCloseAll() : onClose()
                onViewCertificate && onViewCertificate()
              }}
              sx={theme => ({
                backgroundColor: theme.palette.common.black,
                color: theme.palette.common.white,
                py: 1.5,
                '&:hover': { backgroundColor: theme.palette.grey[800] },
              })}
            >
              View certificate
            </Button>
          </Box>
        </Box>
      }
    />
  )
}
